import {
    resolveProfileDestination,
} from "./profile-destination";

const profileRoleTranslationKeys = {
    carrier_owner: "main.header.profile.roles.carrierOwner",
    company_director:
        "main.header.profile.roles.companyDirector",
    vice_carrier_director:
        "main.header.profile.roles.carrierViceDirector",
    carrier_dispatcher:
        "main.header.profile.roles.carrierExpeditor",
    company_person:
        "main.header.profile.roles.companyPerson",
    driver: "main.header.profile.roles.driver",
};

const profileCompanyTypeTranslationKeys = {
    consignor: "main.header.profile.companyTypes.consignor",
    carrier: "main.header.profile.companyTypes.carrier",
    transport_company:
        "main.header.profile.companyTypes.transportCompany",
};

function hasValue(value) {
    return (
        value !== null &&
        value !== undefined &&
        value !== ""
    );
}

function normalizeProfileTerm(value) {
    if (typeof value !== "string") {
        return value;
    }

    return value
        .replace(/Диспетчер/g, "Экспедитор")
        .replace(/диспетчер/g, "экспедитор");
}

function getUserInitials(name) {
    if (typeof name !== "string" || !name.trim()) {
        return "—";
    }

    return name
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
}

function resolveRoleName(userInfo, translate) {
    const translationKey =
        profileRoleTranslationKeys[userInfo.role];

    if (translationKey) {
        return translate(translationKey);
    }

    return normalizeProfileTerm(userInfo.roleName);
}

function resolveOrganizationName(
    userInfo,
    organization,
    translate
) {
    if (organization.name) {
        return organization.name;
    }

    if (organization.context === "platform") {
        return translate("main.header.profile.platform");
    }

    if (
        userInfo.rules &&
        userInfo.rules.IsNeedCreateLegalEntity
    ) {
        return translate(
            "main.header.profile.legalEntityMissing"
        );
    }

    return translate(
        "main.header.profile.organizationMissing"
    );
}

function resolveOrganizationLabel(
    organization,
    translate
) {
    if (organization.context === "consignor") {
        return translate(
            "main.header.profile.consignorOrganization"
        );
    }

    if (organization.context === "company") {
        return translate(
            "main.header.profile.companyOrganization"
        );
    }

    return translate("main.header.profile.organization");
}

function resolveCompanyTypeName(
    organization,
    translate
) {
    const translationKey =
        profileCompanyTypeTranslationKeys[
            organization.typeCode
            ];

    if (translationKey) {
        return translate(translationKey);
    }

    return organization.typeName || organization.type;
}

function compactRows(rows) {
    return rows.filter((row) => hasValue(row.value));
}

export function buildProfileCardViewModel({
                                              userInfo = {},
                                              translate,
                                          }) {
    const t =
        typeof translate === "function"
            ? translate
            : (key) => key;

    const organization = userInfo.organization || {};
    const tariff = userInfo.tariff || null;
    const rating = userInfo.rating || null;

    const organizationName = resolveOrganizationName(
        userInfo,
        organization,
        t
    );

    const legalEntitiesValue =
        organization.legalEntitiesCount === 0
            ? t(
                "main.header.profile.legalEntitiesMissing"
            )
            : organization.legalEntitiesCount;

    const organizationRows = compactRows([
        {
            id: "organization",
            label: resolveOrganizationLabel(
                organization,
                t
            ),
            value: organizationName,
        },
        {
            id: "companyType",
            label: t(
                "main.header.profile.companyType"
            ),
            value: resolveCompanyTypeName(
                organization,
                t
            ),
        },
        {
            id: "legalEntities",
            label: t(
                "main.header.profile.legalEntities"
            ),
            value:
                organization.context === "company"
                    ? legalEntitiesValue
                    : null,
        },
    ]);

    const tariffRows = tariff
        ? compactRows([
            {
                id: "tariff",
                label: t(
                    "main.header.profile.tariff"
                ),
                value: tariff.name,
            },
            {
                id: "tariffStatus",
                label: t(
                    "main.header.profile.tariffStatus"
                ),
                value: tariff.status,
            },
            {
                id: "tariffUntil",
                label: t(
                    "main.header.profile.tariffUntil"
                ),
                value: tariff.activeUntil,
            },
        ])
        : [];

    const ratingRows = rating
        ? compactRows([
            {
                id: "rating",
                label: t(
                    "main.header.profile.rating"
                ),
                value: rating.value,
            },
            {
                id: "ratingReviews",
                label: t(
                    "main.header.profile.ratingReviews"
                ),
                value: rating.reviewsCount,
            },
        ])
        : [];

    const sections = [
        {
            id: "organization",
            rows: organizationRows,
        },
        {
            id: "tariff",
            rows: tariffRows,
        },
        {
            id: "rating",
            rows: ratingRows,
        },
    ].filter((section) => section.rows.length > 0);

    return {
        name: userInfo.fio || "—",
        initials: getUserInitials(userInfo.fio),
        roleName: resolveRoleName(userInfo, t),
        organizationName,
        isStaff: Boolean(userInfo.isStaff),
        cabinetPath: resolveProfileDestination(
            userInfo,
            organization
        ),
        sections,
        labels: {
            workMode: t(
                "main.header.profile.workMode"
            ),
            ownerMode: t(
                "main.header.role.owner"
            ),
            carrierMode: t(
                "main.header.role.carrier"
            ),
            openCabinet: t(
                "main.header.profile.openCabinet"
            ),
            logout: t("main.header.logout"),
            openProfile: t(
                "main.header.profile.open"
            ),
        },
    };
}